<script lang="ts" setup>
import type { ResponseResult } from '~kui/types/formResult';

import { Button } from '~ds/components/ui/button';
import { Text } from '~ds/components/ui/typography';
import { defineField, defineFormConfig } from '~kui/components/KUI/Core/Form/util/form';
import { useI18n } from 'vue-i18n';
import { z } from 'zod';

/**
 * The consumer resolves `termsPath`, because only the app knows the cluster path prefix (a bare
 * `/terms` would always land on the root cluster), and passes `marketingOptInDefault` from the
 * picked state's server-decided `marketingBoxStartsTicked`. `ageClaimRequired` is off only once the
 * about-you step has collected a birth month; without one the tick is the sign-up's only age
 * control. `email` is the address the account is being created for, shown beside "Wrong email?".
 */
interface Props {
  result?: ResponseResult | null;
  loading?: boolean;
  termsPath?: string;
  showMarketingOptIn?: boolean;
  marketingOptInDefault?: boolean;
  ageClaimRequired?: boolean;
  email?: string;
}

const props = withDefaults(defineProps<Props>(), {
  result: null,
  loading: false,
  termsPath: '/terms',
  showMarketingOptIn: false,
  marketingOptInDefault: false,
  ageClaimRequired: true,
  email: '',
});

const emit = defineEmits<{
  submit: [payload: { marketingOptIn: boolean | null }];
  changeEmail: [];
}>();

const { t } = useI18n();

// Read once: a box the student never saw must never be recorded as their answer, and the
// student answers the box as first shown.
const showBox = props.showMarketingOptIn;
const boxDefault = props.marketingOptInDefault;

const marketingField = {
  marketingOptIn: defineField({
    type: 'checkbox',
    options: {
      label: t('unified.onboarding.marketingOptIn'),
      dataTestId: 'onboarding-marketing-opt-in',
    },
    initialValue: boxDefault,
    schema: z.boolean(),
  }),
};

const consentField = {
  consent: defineField({
    type: 'checkbox',
    options: {
      label: t(props.ageClaimRequired ? 'unified.onboarding.termsLabelWithAge' : 'unified.onboarding.termsLabel', {
        termsLink: `<a href="${props.termsPath}" target="_blank" rel="noopener noreferrer" class="text-foreground font-medium underline">${t('unified.onboarding.termsLinkText')}</a>`,
      }),
      htmlLabel: true,
      required: true,
      dataTestId: 'onboarding-terms-consent',
    },
    initialValue: false,
    schema: z.literal(true, t('unified.onboarding.termsRequired')),
  }),
};

const formConfig = defineFormConfig(
  showBox ? { ...marketingField, ...consentField } : consentField,
  t('general.auth.submitButton.submit'),
);

const handleSubmit = (values: { consent: boolean; marketingOptIn?: boolean }) => {
  emit('submit', { marketingOptIn: showBox ? (values.marketingOptIn ?? false) : null });
};
</script>

<template>
  <div data-testid="onboarding-terms-step">
    <KUICoreForm
      :form-config="formConfig"
      :form-result="result"
      :loading="loading"
      @submit="handleSubmit"
    />
    <div class="mt-4 flex flex-col items-center gap-2">
      <Text
        v-if="email"
        size="sm"
        class="text-center"
        data-testid="onboarding-terms-email"
      >
        <i18n-t keypath="unified.onboarding.signingUpAs">
          <template #email>
            <span class="font-semibold break-all">{{ email }}</span>
          </template>
        </i18n-t>
      </Text>
      <Button
        data-testid="onboarding-terms-change-email"
        size="sm"
        variant="outline"
        type="button"
        :disabled="loading"
        @click="emit('changeEmail')"
      >
        {{ t('general.auth.changeEmailButton') }}
      </Button>
    </div>
  </div>
</template>
